import React, { type PropsWithChildren, useCallback } from 'react';
import renderIf from 'render-if';
import classnames from 'classnames';
import { withContentRect } from 'react-measure';
import { throttle } from 'lodash';
import ScaleDates from './ScaleDates';
import { dateMax, dateMin } from 'javascripts/util/dateFns';
import { clampDate, onScroll } from './helpers';
import styles from './ScaleBar.module.css';

interface BarWithDatesProps {
  measureRef: () => void;
  contentRect: {
    offset: {
      width: number;
    };
  };
  startDate: Date;
  endDate: Date;
  visibleStartDate: Date;
  visibleEndDate: Date;
  onSetVisibleDates: (startDate: string, endDate: string) => void;
  publishing: boolean;
  hideWeekends: boolean;
  fiscalMonth: number;
  fiscalYearFormat?: string;
  timescale: string;
  hideMinorInterval?: boolean;
}

const onScrollHandler = throttle(onScroll, 16);
const BarWithDates: React.FC<PropsWithChildren<BarWithDatesProps>> = props => {
  const {
    hideWeekends,
    hideMinorInterval,
    contentRect: {
      offset: { width },
    },
    publishing,
    timescale,
    fiscalMonth,
    fiscalYearFormat,
    measureRef,
    onSetVisibleDates,
    children,
  } = props;

  const startDate = clampDate(props.startDate);
  const endDate = clampDate(props.endDate);
  const visibleStartDate = dateMax(startDate, props.visibleStartDate);
  const visibleEndDate = dateMin(endDate, props.visibleEndDate);

  // When the visible date is updated, do it on the next animation frame to make visuals smooth
  const onSetVisibleDatesHandler = useCallback(
    (visibleStartDate, visibleEndDate) => {
      requestAnimationFrame(() => {
        onSetVisibleDates(visibleStartDate, visibleEndDate);
      });
    },
    [onSetVisibleDates]
  );

  // On scroll handler for the scaleDates component
  const onScroll = useCallback(
    offset =>
      onScrollHandler(offset, {
        endDate,
        hideWeekends,
        onSetVisibleDatesHandler,
        startDate,
        visibleEndDate,
        visibleStartDate,
        width,
      }),
    [
      endDate,
      hideWeekends,
      onSetVisibleDatesHandler,
      startDate,
      visibleEndDate,
      visibleStartDate,
      width,
    ]
  );

  // Handle first render measurement
  if (!width) {
    return (
      <div ref={measureRef} className={styles.scaleBar} data-async-loading />
    );
  }

  return (
    <div
      ref={measureRef}
      aria-label='scale bar'
      className={classnames(
        styles.barWithDates,
        publishing && styles.scaleBarPublishing
      )}
    >
      {renderIf(!publishing && children)(<>{children}</>)}

      <ScaleDates
        startDate={startDate}
        endDate={endDate}
        visibleStartDate={visibleStartDate}
        visibleEndDate={visibleEndDate}
        visibleWidth={width}
        fiscalMonth={fiscalMonth}
        fiscalYearFormat={fiscalYearFormat}
        hideWeekends={hideWeekends}
        timescale={timescale}
        onScroll={onScroll}
        hideMinorInterval={hideMinorInterval}
      />
    </div>
  );
};

const memoBarWithDates = React.memo(BarWithDates);

export { memoBarWithDates as BarWithDates }; // This is primarily used for testing

export default withContentRect('offset')(memoBarWithDates);
